import React from "react";
import { FormikContext, FormikHelpers, FormikState, useFormik } from "formik";
import * as Yup from "yup";

import { useToastUpdater } from "../../../../../context/toastContext/toastContext";
import actionsService, { ActionCommentPost } from "../../../../../services/actions.service";
import useActionComments from "../../../../../hooks/data/useActionComments";

import Button from "../../../../common/Buttons/Button/Button";
import Icon from "../../../../common/Icon/Icon";
import Input from "../../../../common/Form/Input/Input";
import Typography from "../../../../common/Typography/Typography";

export type OnCommentAdded = (comment: string) => void;

const updateSiteFormSchema = Yup.object().shape({ comment: Yup.string().required() });

interface Props {
    actionId: string;
    onCommentAdded?: OnCommentAdded;
}

const CommentForm = ({ actionId, onCommentAdded }: Props) => {
    const { addToast } = useToastUpdater();
    const { mutateActionComment } = useActionComments(actionId as string);

    const initialValues: ActionCommentPost = {
        comment: "",
        actionId,
        close: false,
    };

    const formikHook = useFormik({
        validationSchema: updateSiteFormSchema,
        initialValues,
        onSubmit: async (values: ActionCommentPost, { setSubmitting, resetForm }: FormikHelpers<ActionCommentPost>) => {
            setSubmitting(true);

            try {
                const response = await actionsService.addComment(values);
                await mutateActionComment(response.comment);

                if (onCommentAdded) {
                    onCommentAdded(values.comment);
                }

                setSubmitting(false);
                resetForm(initialValues as Partial<FormikState<ActionCommentPost>>);
            } catch (error) {
                addToast("Your comment could not be added. Please try again", "error", "");
                setSubmitting(false);
            }
        },
    });

    const { errors, values, touched, handleSubmit, isSubmitting, handleChange, handleBlur } = formikHook;

    return (
        <FormikContext.Provider value={formikHook}>
            <form onSubmit={handleSubmit} className="flex flex-row gap-0.5 w-full">
                <Input
                    id="comment"
                    placeholder="Add a comment..."
                    name="comment"
                    value={values.comment}
                    error={touched.comment && !!errors.comment}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
                <Button
                    variant="secondary"
                    type="submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    className="text-white!"
                    title="Send"
                >
                    <Typography variant="note" color="mint" className="text-[0.6rem]">
                        Send
                    </Typography>
                    <Icon icon="arrow_right" width={28} height={28} />
                </Button>
            </form>
        </FormikContext.Provider>
    );
};

export default CommentForm;
